import { FC } from "react";
import { useNavigate } from "react-router-dom";
import { withPage } from "../../components/Page";
import { PageHeader } from "../../components/PageHeader";
import { QuickLinkTile } from "../../components/QuickLinkTile";
import {
  DrawingScheduleTable,
  TsaFloorPlanRow,
  AdditionalRecordRow,
} from "../../components/DrawingScheduleTable";
import { DrawingCollectionCard } from "../../components/DrawingCollectionCard";
import { InfoCallout } from "../../components/InfoCallout";
import styles from "./Drawings.module.scss";
import { clientContent } from "../../client/content";

const TSA_ROWS: TsaFloorPlanRow[] = [
  {
    id: "tsa-b01",
    drawingFile: "Ductwork Services Plan",
    drawingHref: "/documents/drawings/table/ductwork-sample.webp",
    source: "TSA Archive",
    titleLevel: "Basement / Plant Level",
    discipline: "Mechanical",
    compiledBy: "TSA",
    date: "2023",
  },
  {
    id: "tsa-bm01",
    drawingFile: "Electrical Distribution Layout",
    drawingHref: "/documents/drawings/table/electrical-sample.webp",
    source: "TSA Archive",
    titleLevel: "Basement Mezzanine",
    discipline: "Electrical",
    compiledBy: "TSA",
    date: "2023",
  },
  {
    id: "tsa-lg01",
    drawingFile: "Heating Distribution Schematic",
    drawingHref: "/documents/drawings/table/heating-sample.webp",
    source: "TSA Archive",
    titleLevel: "Lower Ground Floor",
    discipline: "Mechanical",
    compiledBy: "TSA",
    date: "2023",
  },
  {
    id: "tsa-g01",
    drawingFile: "Low Temperature Hot Water Layout",
    drawingHref: "/documents/drawings/table/heating-sample-2.webp",
    source: "TSA Archive",
    titleLevel: "Ground Floor",
    discipline: "Mechanical",
    compiledBy: "TSA",
    date: "2024",
  },
  {
    id: "tsa-101",
    drawingFile: "Lighting & Small Power Plan",
    drawingHref: "/documents/drawings/table/lighting-sample.webp",
    source: "TSA Archive",
    titleLevel: "Level 01",
    discipline: "Electrical",
    compiledBy: "TSA",
    date: "2024",
  },
  {
    id: "tsa-201",
    drawingFile: "Chilled Water Pipework Layout",
    drawingHref: "/documents/drawings/table/pipework-sample-2.webp",
    source: "TSA Archive",
    titleLevel: "Level 02",
    discipline: "Mechanical",
    compiledBy: "TSA",
    date: "2024",
  },
  {
    id: "tsa-301",
    drawingFile: "Main Plantroom Layout",
    drawingHref: "/documents/drawings/table/plantroom-sample.webp",
    source: "TSA Archive",
    titleLevel: "Level 03 Plant",
    discipline: "Mechanical",
    compiledBy: "TSA",
    date: "2024",
  },
  {
    id: "tsa-302",
    drawingFile: "Secondary Plantroom Schematic",
    drawingHref: "/documents/drawings/table/plantroom-sample-1.webp",
    source: "TSA Archive",
    titleLevel: "Level 04 Plant Room",
    discipline: "Mechanical",
    compiledBy: "TSA",
    date: "2024",
  },
  {
    id: "tsa-401",
    drawingFile: "Roof Plant & Structural Layout",
    drawingHref: "/documents/drawings/table/roof-plant-sample.webp",
    source: "TSA Archive",
    titleLevel: "Roof Plant Level",
    discipline: "Structural",
    compiledBy: "TSA",
    date: "2024",
  },
  {
    id: "tsa-501",
    drawingFile: "Underfloor Heating Zone Plan",
    drawingHref: "/documents/drawings/table/ufh-sample.webp",
    source: "TSA Archive",
    titleLevel: "Lower Ground UFH Zone",
    discipline: "Mechanical",
    compiledBy: "TSA",
    date: "2024",
  },
  {
    id: "tsa-ur01",
    drawingFile: "Public Health Water Services",
    drawingHref: "/documents/drawings/table/water-services-sample.webp",
    source: "TSA Archive",
    titleLevel: "Upper Roof Services",
    discipline: "Public Health",
    compiledBy: "TSA",
    date: "2024",
  },
];

const ADDITIONAL_ROWS: AdditionalRecordRow[] = [
  {
    id: "add-1",
    filename: "6000-RDM-S-903 Gas Schematic.pdf",
    fileType: "PDF",
    description: "Mechanical Services Gas System Schematic",
    discipline: "Mechanical",
  },
  {
    id: "add-2",
    filename: "9703-601.pdf",
    fileType: "PDF",
    description: "LV Distribution System LV02–LV04",
    discipline: "Electrical",
  },
  {
    id: "add-3",
    filename: "9703-601_AB.dwg",
    fileType: "DWG",
    description: "LV Distribution System LV02–LV04",
    discipline: "Electrical",
  },
];

const Drawings: FC = () => {
  const navigate = useNavigate();

  return (
    <div className="page-container-right">
      <PageHeader
        eyebrow="DRAWINGS / COMMERZBANK"
        title="Drawing schedule"
        subtitle="Floor plans, key schematics and linked drawing collections."
        secondaryAction={{
          label: "Export schedule",
          href: "/more-info",
        }}
        primaryAction={{
          label: "Search drawings",
          href: "/search",
          onClick: (e) => {
            e.preventDefault();
            navigate("/search");
          },
        }}
      />

      <section
        className={styles.keySchematicsSection}
        aria-labelledby="key-schematics-heading"
      >
        <div className={styles.sectionHeaderRow}>
          <div>
            <h2 id="key-schematics-heading" className={styles.sectionTitle}>
              Key schematics
            </h2>
            <p className={styles.sectionSubtitle}>
              Direct access to frequently needed drawing information
            </p>
          </div>
        </div>

        <div className={styles.quickLinksGrid}>
          <QuickLinkTile
            title="ChW schematic"
            subtitle="Mechanical services"
            icon="~"
            href="/documents/drawings/schematics/schematic-sample.webp"
            onClick={(e) => {
              e.preventDefault();
              window.open("/documents/drawings/schematics/schematic-sample.webp", "_blank", "noopener,noreferrer");
            }}
          />
          <QuickLinkTile
            title="ChW riser schematic"
            subtitle="Mechanical services"
            icon="↑"
            href="/documents/drawings/schematics/schematic-sample-1.webp"
            onClick={(e) => {
              e.preventDefault();
              window.open("/documents/drawings/schematics/schematic-sample-1.webp", "_blank", "noopener,noreferrer");
            }}
          />
          <QuickLinkTile
            title="LTHW schematic"
            subtitle="Mechanical services"
            icon="~"
            href="/documents/drawings/schematics/schematic-sample-2.webp"
            onClick={(e) => {
              e.preventDefault();
              window.open("/documents/drawings/schematics/schematic-sample-2.webp", "_blank", "noopener,noreferrer");
            }}
          />
          <QuickLinkTile
            title="LV schematics"
            subtitle="LV01–LV04"
            icon="⚡"
            href="/documents/drawings/schematics/schematic-sample-3.webp"
            onClick={(e) => {
              e.preventDefault();
              window.open("/documents/drawings/schematics/schematic-sample-3.webp", "_blank", "noopener,noreferrer");
            }}
          />
          <QuickLinkTile
            title="Fire & ventilation"
            subtitle="Fire alarm, dampers and ventilation"
            icon="○"
            href="/more-info"
          />
        </div>
      </section>

      <div className={styles.mainLayoutGrid}>
        <main className={styles.mainColumn}>
          <section
            className={styles.contentSection}
            aria-labelledby="tsa-floor-plans-heading"
          >
            <div className={styles.sectionHeaderRow}>
              <div>
                <h2
                  id="tsa-floor-plans-heading"
                  className={styles.sectionTitle}
                >
                  TSA floor plans
                </h2>
                <p className={styles.sectionSubtitle}>
                  General floor plans in AutoCAD and PDF format.
                </p>
              </div>
              <div className={styles.filterControlsRow}>
                <button
                  type="button"
                  className={styles.filterChip}
                  aria-haspopup="listbox"
                >
                  Floor: <span>All</span> ▾
                </button>
                <button
                  type="button"
                  className={styles.filterChip}
                  aria-haspopup="listbox"
                >
                  Format: <span>All</span> ▾
                </button>
              </div>
            </div>

            <DrawingScheduleTable
              variant="tsa"
              columns={[
                "DRAWING FILE",
                "SOURCE",
                "TITLE / LEVEL",
                "DISCIPLINE",
                "COMPILED BY",
                "DATE",
              ]}
              tsaRows={TSA_ROWS}
              ariaLabel="TSA Floor Plans Schedule"
            />

            <InfoCallout variant="note">
              <strong>Floor-plan record:</strong> these will be YOUR drawings. We import what you provide and sort in a logical order.
            </InfoCallout>
          </section>

          <section
            className={styles.contentSection}
            aria-labelledby="additional-records-heading"
          >
            <div className={styles.sectionHeaderRow}>
              <div>
                <h2
                  id="additional-records-heading"
                  className={styles.sectionTitle}
                >
                  Additional drawing records
                </h2>
                <p className={styles.sectionSubtitle}>
                  Archive files are included here rather than hidden behind a
                  separate destination.
                </p>
              </div>
            </div>

            <DrawingScheduleTable
              variant="additional"
              columns={["FILENAME", "DESCRIPTION", "DISCIPLINE"]}
              additionalRows={ADDITIONAL_ROWS}
              ariaLabel="Additional Drawing Records Table"
            />
          </section>
        </main>

        <aside
          className={styles.sidebarColumn}
          aria-label="Drawing collections"
        >
          <div className={styles.sectionHeaderRow}>
            <div>
              <h2 className={styles.sectionTitle}>Drawing collections</h2>
              <p className={styles.sectionSubtitle}>
                Browse material by its existing logical grouping.
              </p>
            </div>
          </div>

          <div className={styles.collectionsContainer}>
            <DrawingCollectionCard
              title="XREFs"
              subtext="Referenced drawing files"
              description="Cross-reference records grouped by floor and discipline."
              href="/more-info"
              icon="↗"
            />
            <DrawingCollectionCard
              title="Base build drawings"
              subtext="Core building drawing set"
              description="Base-build plans, schematics and coordinated records."
              href="/more-info"
              icon="▦"
            />
            <DrawingCollectionCard
              title="Post 2005 fitout drawings"
              subtext="Fitout drawing collections"
              description="Browse by discipline rather than through one long undifferentiated list."
              href="/more-info"
              icon="+"
              sublinks={[
                { label: "Mechanical & sprinkler", href: "/more-info" },
                { label: "Electrical & communication", href: "/more-info" },
                { label: "Control", href: "/more-info" },
                { label: "Building", href: "/more-info" },
              ]}
            />
            <DrawingCollectionCard
              title="Included archive records"
              subtext="3 files on this page"
              description="Small archive content is kept visible in the additional drawing records table."
              nonClickable={true}
              icon="▫"
            />
          </div>
        </aside>
      </div>
    </div>
  );
};

export default withPage(Drawings)({
  meta: {
    title: "Drawing schedule | Commerzbank | Pulse",
    description:
      "Floor plans, key schematics and linked drawing collections for Commerzbank site.",
  },
  breadcrumbs: [
    { label: clientContent.systemName, href: "/" },
    { label: "Drawings" },
  ],
  searchPlaceholder: "Search drawings and documents",
});
